import { useEffect, useRef, useState } from "react";
import { useDarkMode } from "@/hooks/useDarkMode";
import {
  useI18N,
  useCreateList,
  usePostCreateListEmpty,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { createPageAnalytics, trackEvent } from "common/analytics";
import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { XIcon, ArrowLeft } from "lucide-react";
import WideButton from "@/components/common/WideButton";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";

interface CreateListProps {
  onClose: () => void;
  businessId: number;
  venueDetails: any;
  fullSize?: boolean;
  onCreateList: (listName: string) => void;
}

const CreateList = ({
  onClose,
  businessId,
  venueDetails,
  onCreateList,
}: CreateListProps) => {
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const showToast = useCustomToast();

  const [listName, setListName] = useState("");
  const { error, createList, isLoading, result } = useCreateList();
  const {
    error: emptyError,
    createList: createEmptyList,
    isLoading: isEmptyLoading,
    result: emptyResult,
  } = usePostCreateListEmpty();

  useEffect(() => {
    if (result) {
      // Outcome event — fired on the create-success branch.
      trackEvent("saved_list_created", { save_list_name: listName });
      showToast(i18n.get("addedToYourSavedPlaces"), "success");
      onClose();
    }
  }, [result]);

  useEffect(() => {
    if (emptyResult) {
      // Outcome event — fired on the create-success branch (empty-list path).
      trackEvent("saved_list_created", { save_list_name: listName });
      showToast(i18n.get("addedToYourSavedPlaces"), "success");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyResult]);

  useEffect(() => {
    if (error) {
      console.log("error", error);

      showToast(error.message, "error");
    }
  }, [error, isDark, venueDetails]);

  useEffect(() => {
    if (emptyError) {
      console.log("error", emptyError);

      showToast(emptyError.message, "error");
    }
  }, [emptyError]);

  const handleSave = () => {
    console.log("handleSave", businessId);
    if (!businessId) {
      console.log("no businessId", businessId);
      return;
    }

    if (!listName) {
      console.log("no listName", listName);
      return;
    }

    createList({
      name: listName,
      venueId: businessId,
    });

    onCreateList(listName);
  };

  const handleEmptyListCreate = () => {
    if (!listName) {
      console.log("no listName", listName);
      return;
    }

    createEmptyList({
      name: listName,
    });
  };

  const closeButtonRef = useRef<HTMLButtonElement>(null); // Reference for initial focus

  // Focus on the close button when the component mounts
  useEffect(() => {
    setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 100);
  }, []);

  // page_view_page -> create_saved_list_form_opened. This component is mounted only while the
  // form is shown, so mount == opened.
  // Ref-guarded so React StrictMode's double-invoke of effects in dev doesn't fire it twice.
  const openTrackedRef = useRef(false);
  useEffect(() => {
    if (openTrackedRef.current) return;
    openTrackedRef.current = true;
    createPageAnalytics("create_saved_list_form").track("page_view_page", {});
  }, []);

  return (
    <section
      style={modalStyle}
      className={cn(modalClassName)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {isLoading && <Loader />}
      
      {/* Screen reader announcements for errors */}
      <div role="alert" aria-live="assertive" className="sr-only">
        {error && error.message}
        {emptyError && emptyError.message}
      </div>

      <header
        className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          ref={closeButtonRef}
          onClick={() => {
            createPageAnalytics("create_saved_list_form").track("close_action_button", {});
            onClose();
          }}
          variant="closeModal"
          icon={
            venueDetails ? (
              <ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} aria-hidden="true" />
            ) : (
              <XIcon color={isDark ? "#A39FE8" : "#352FC3"} aria-hidden="true" />
            )
          }
          aria-label="Close modal"
          tabIndex={0}
          className="flex-shrink-0"
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
          id="modal-title"
        >
          {i18n.get("createList")}
        </h1>
        <div className="flex-shrink-0 w-10"></div>{" "}
        {/* Spacer to balance the layout */}
      </header>

      <div
        aria-label="Modal Content"
        role="none"
        className="flex-1 overflow-y-auto py-3 px-3"
      >
        <div className="px-4 md:px-6 py-6 flex flex-col gap-6">
          <div className="flex flex-col gap-4 mb-2">
            <FloatingLabelInput
              label={i18n.get("yourListName")}
              required
              type="text"
              value={listName}
              placeholder={i18n.get("enterYourListName")}
              onChange={(event) => {
                setListName(event.target.value);
              }}
              maxLength={50}
            />
          </div>

          <div className="mb-[10px] mt-[2px] flex-row">
            <div
              id="charCount"
              className="label-s-medium text-fg-neutral-subtle mt-3"
            >
              <span className={listName.length < 5 ? "text-red-600" : ""}>
                {listName.length}
              </span>
              /50 {i18n.get("characters")} - {i18n.get("atLeast3CharactersRequired")}
            </div>
            
            {/* Screen reader announcement for character count */}
            <div role="status" aria-live="polite" className="sr-only">
              {listName.length > 0 && `${listName.length} of 50 characters`}
            </div>
          </div>
        </div>
      </div>

      <footer className="py-3 min-h-14 flex items-center justify-center text-center border-border-token-brand-minimal bg-bg-brand-secondary-default sticky bottom-0 z-10 px-7 md:px-9">
        <div className="flex-col justify-end items-center gap-4 flex w-full">
          <WideButton
            className="grow"
            disabled={listName.length < 3}
            onClick={() => {
              if (businessId) {
                handleSave();
              } else {
                handleEmptyListCreate();
              }
            }}
          >
            {i18n.get("create")}
          </WideButton>
        </div>
      </footer>
    </section>
  );
};

export default CreateList;
